'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import ModuleHeader, { type ModuleHeaderProps, type TypeFilterTab } from './ModuleHeader';
import StrictModuleHeader, { type StrictModuleHeaderProps, type StrictPill } from './StrictModuleHeader';
import { StrictNewActionContext, type StrictGridNewAction } from './StrictDataGrid';
import { useSetNavTypeFilters } from '@/components/shared/navTypeFilterContext';
import { TourPrompt, type TourPromptConfig, type TourPromptFlowInfo, GuidedTour } from '@/components/training/guided-tour';
import type { ViewScope } from '@/config/guided-tours/types';
import { useModuleColor } from '@/hooks/useModuleColor';

const LAYOUT_PREFS_KEY = 'ta_ui_layout_prefs';
const LAYOUT_PREFS_EVENT = 'ta-ui-layout-prefs-changed';

function readNavLayoutMode(): 'horizontal' | 'vertical' {
  if (typeof window === 'undefined') return 'horizontal';
  try {
    const prefs = JSON.parse(localStorage.getItem(LAYOUT_PREFS_KEY) || '{}');
    return prefs.navLayoutMode === 'vertical' ? 'vertical' : 'horizontal';
  } catch {
    return 'horizontal';
  }
}

interface ModulePageShellProps {
  headerProps?: ModuleHeaderProps | null;
  /** Alternative to `headerProps`: renders a StrictModuleHeader instead of
   *  ModuleHeader. When provided, this takes precedence over `headerProps`.
   *  Pills are published to the NavTypeFilterContext for vertical-nav mode. */
  strictHeaderProps?: StrictModuleHeaderProps | null;
  /** Fully custom header node (takes precedence over both header props).
   *  `pills`/`activePill`/`onPillChange` mirror strictHeaderProps so the
   *  shell can still publish them to NavTypeFilterContext in vertical-nav
   *  mode — the custom header should hide its own nav in that mode. */
  customHeader?: {
    node: React.ReactNode;
    pills?: StrictPill[];
    activePill?: string | null;
    onPillChange?: (id: string | null) => void;
  } | null;
  filterBar?: React.ReactNode;
  children: React.ReactNode;
  showSurface?: boolean;
  className?: string;
  filterBarClassName?: string;
  surfaceClassName?: string;
  contentClassName?: string;
  /** Module key (e.g. 'total-finance', 'total-connect') — when provided,
   *  ModulePageShell fetches the module color from the database via
   *  useModuleColor and injects it into headerProps.primaryColor if not
   *  already set. Also passed through to ModuleHeader for its own use. */
  moduleKey?: string;
  /** When true, type filter tabs in headerProps are NOT published to the
   *  NavTypeFilterContext, so they won't appear as grandchildren in the
   *  UnifiedSideNav vertical nav. They still render in the ModuleHeader. */
  suppressNavTypeFilters?: boolean;
  /** Optional proactive tour prompt configuration. When provided, renders a
   *  TourPrompt popup that offers to show the user around the current section. */
  tourPromptConfig?: TourPromptConfig | null;
  /** Stable context key for the tour prompt dismissal (e.g. 'attendance:shifts'). */
  tourPromptContextKey?: string;
  /** Optional: list of available flows for next-tour suggestion. */
  tourPromptAvailableFlows?: TourPromptFlowInfo[];

  /** When provided, mounts the AI onboarding coach scoped to the current
   *  view. The coach is triggered via the 'ai-coach-open' global event
   *  (dispatched by the UnifiedSideNav "AI Training Coach" submenu button).
   *  It guides the user through features of the current view only — it does
   *  not navigate away. */
  onboardingViewScope?: ViewScope;
}

export default function ModulePageShell({
  headerProps,
  strictHeaderProps,
  customHeader,
  filterBar,
  children,
  showSurface = true,
  className,
  filterBarClassName,
  surfaceClassName,
  contentClassName,
  moduleKey,
  suppressNavTypeFilters = false,
  tourPromptConfig,
  tourPromptContextKey,
  tourPromptAvailableFlows,
  onboardingViewScope,
}: ModulePageShellProps) {
  const setTypeFilters = useSetNavTypeFilters();
  const [navLayoutMode, setNavLayoutMode] = useState<'horizontal' | 'vertical'>(readNavLayoutMode);

  // "+ New" registrations from descendant StrictDataGrids — the header
  // renders the first one as its New button (Row A, same place always).
  const [newActions, setNewActions] = useState<Map<symbol, StrictGridNewAction>>(new Map());
  const newActionCtxValue = useMemo(() => ({
    register: (action: StrictGridNewAction) => {
      const key = Symbol('grid-new');
      setNewActions((prev) => new Map(prev).set(key, action));
      return () => setNewActions((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    },
  }), []);
  const registeredNewAction = newActions.size > 0 ? Array.from(newActions.values())[0] : undefined;

  // Fetch module color from database when moduleKey is provided.
  // The color is injected into headerProps.primaryColor if not already set,
  // and also passed through to ModuleHeader via headerProps.moduleKey so it
  // can use the color for its own styling (favourites, tour, etc.).
  const { moduleColor: fetchedModuleColor } = useModuleColor(moduleKey ?? '');

  // Merge the fetched module color into headerProps — but only when the
  // caller passed a shell-level `moduleKey`. Without one, useModuleColor('')
  // returns the default fallback (#3b82f6); injecting that as primaryColor
  // would clobber the header's own useModuleColor(headerProps.moduleKey)
  // resolution and pin every header to the default blue.
  const resolvedHeaderProps = useMemo(() => {
    if (!headerProps || !moduleKey) return headerProps;
    const hasPrimaryColor = headerProps.primaryColor != null && headerProps.primaryColor !== '';
    const hasModuleKey = headerProps.moduleKey != null && headerProps.moduleKey !== '';
    if (hasPrimaryColor && hasModuleKey) return headerProps;
    return {
      ...headerProps,
      primaryColor: hasPrimaryColor ? headerProps.primaryColor : (fetchedModuleColor || undefined),
      moduleKey: hasModuleKey ? headerProps.moduleKey : moduleKey,
    };
  }, [headerProps, fetchedModuleColor, moduleKey]);

  // Merge the fetched module color into strictHeaderProps (same logic)
  const resolvedStrictHeaderProps = useMemo(() => {
    if (!strictHeaderProps || !moduleKey) return strictHeaderProps;
    const hasPrimaryColor = strictHeaderProps.primaryColor != null && strictHeaderProps.primaryColor !== '';
    const hasModuleKey = strictHeaderProps.moduleKey != null && strictHeaderProps.moduleKey !== '';
    if (hasPrimaryColor && hasModuleKey) return strictHeaderProps;
    return {
      ...strictHeaderProps,
      primaryColor: hasPrimaryColor ? strictHeaderProps.primaryColor : (fetchedModuleColor || undefined),
      moduleKey: hasModuleKey ? strictHeaderProps.moduleKey : moduleKey,
    };
  }, [strictHeaderProps, fetchedModuleColor, moduleKey]);

  // Merge the first registered "+ New" grid action into strictHeaderProps —
  // renders as the header's New button (strictActions cap still applies).
  const strictHeaderPropsWithNew = useMemo(() => {
    if (!resolvedStrictHeaderProps || !registeredNewAction) return resolvedStrictHeaderProps;
    return {
      ...resolvedStrictHeaderProps,
      showNewButton: true,
      onNew: registeredNewAction.onClick,
      newButtonLabel: registeredNewAction.label,
      newButtonDataTourId: registeredNewAction.dataTourId,
    };
  }, [resolvedStrictHeaderProps, registeredNewAction]);

  // Map strict pills → TypeFilterTab shape for NavTypeFilterContext publishing
  const strictPillsAsTypeFilterTabs = useMemo<TypeFilterTab[] | undefined>(() => {
    if (!strictHeaderPropsWithNew?.pills?.length) return undefined;
    return strictHeaderPropsWithNew.pills.map((p) => ({
      id: p.id,
      label: p.label,
      icon: p.icon,
      accentColor: p.accentColor,
      dataTourId: p.dataTourId,
      badge: p.badge,
      badgeColor:
        p.badgeTone === 'success' ? 'green'
          : p.badgeTone === 'warning' ? 'amber'
            : p.badgeTone === 'danger' ? 'red'
              : p.badgeTone === 'info' ? 'blue'
                : undefined,
    }));
  }, [strictHeaderPropsWithNew?.pills]);

  // Listen for nav layout mode changes (toggled from ModuleHeader or UnifiedSideNav)
  useEffect(() => {
    const handlePrefsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ changedKeys?: string[] }>).detail;
      if (!detail?.changedKeys || detail.changedKeys.includes('navLayoutMode')) {
        setNavLayoutMode(readNavLayoutMode());
      }
    };
    const handleStorage = () => setNavLayoutMode(readNavLayoutMode());
    window.addEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(LAYOUT_PREFS_EVENT, handlePrefsChanged as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  // Publish type filter tabs to NavTypeFilterContext so UnifiedSideNav can render them
  // as grandchildren — but ONLY when vertical nav mode is active. In horizontal mode
  // the tabs render as pills inside ModuleHeader, so we clear the context to avoid
  // them leaking into the side nav.
  useEffect(() => {
    if (suppressNavTypeFilters || navLayoutMode !== 'vertical') {
      setTypeFilters({
        typeFilterTabs: [],
        activeTypeFilter: null,
        onTypeFilterChange: () => {},
      });
      return;
    }
    // Strict header pills take precedence when present
    if (strictPillsAsTypeFilterTabs && strictPillsAsTypeFilterTabs.length > 0) {
      setTypeFilters({
        typeFilterTabs: strictPillsAsTypeFilterTabs,
        activeTypeFilter: resolvedStrictHeaderProps?.activePill ?? null,
        onTypeFilterChange: resolvedStrictHeaderProps?.onPillChange ?? (() => {}),
      });
    } else if (customHeader?.pills && customHeader.pills.length > 0) {
      setTypeFilters({
        typeFilterTabs: customHeader.pills.map((p) => ({
          id: p.id,
          label: p.label,
          icon: p.icon,
          accentColor: p.accentColor,
          dataTourId: p.dataTourId,
          badge: p.badge,
        })),
        activeTypeFilter: customHeader.activePill ?? null,
        onTypeFilterChange: customHeader.onPillChange ?? (() => {}),
      });
    } else if (resolvedHeaderProps?.typeFilterTabs && resolvedHeaderProps.typeFilterTabs.length > 0) {
      setTypeFilters({
        typeFilterTabs: resolvedHeaderProps.typeFilterTabs,
        activeTypeFilter: resolvedHeaderProps.activeTypeFilter ?? null,
        onTypeFilterChange: resolvedHeaderProps.onTypeFilterChange ?? (() => {}),
      });
    } else {
      setTypeFilters({
        typeFilterTabs: [],
        activeTypeFilter: null,
        onTypeFilterChange: () => {},
      });
    }
  }, [
    suppressNavTypeFilters,
    navLayoutMode,
    strictPillsAsTypeFilterTabs,
    customHeader,
    resolvedStrictHeaderProps?.activePill,
    resolvedStrictHeaderProps?.onPillChange,
    resolvedHeaderProps?.typeFilterTabs,
    resolvedHeaderProps?.activeTypeFilter,
    resolvedHeaderProps?.onTypeFilterChange,
    setTypeFilters,
  ]);

  return (
    <StrictNewActionContext.Provider value={newActionCtxValue}>
    <div className={cn('h-full min-h-0 flex flex-col bg-gray-50 dark:bg-gray-950', className)}>
      {customHeader ? (
        customHeader.node
      ) : strictHeaderPropsWithNew ? (
        <StrictModuleHeader
          {...strictHeaderPropsWithNew}
          showCombinedLayoutToggle={strictHeaderPropsWithNew.showCombinedLayoutToggle ?? true}
        />
      ) : resolvedHeaderProps ? (
        <ModuleHeader
          {...resolvedHeaderProps}
          showCombinedLayoutToggle={resolvedHeaderProps.showCombinedLayoutToggle ?? true}
        />
      ) : null}

      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden',
          showSurface && 'border-x border-b border-gray-200 bg-white shadow-none dark:border-gray-700 dark:bg-gray-900/40',
          surfaceClassName,
        )}
      >
        {filterBar ? (
          <div
            className={cn(
              'flex-shrink-0',
              showSurface && 'border-b border-gray-200 dark:border-gray-700',
              filterBarClassName,
            )}
          >
            {filterBar}
          </div>
        ) : null}

        <div className={cn('flex-1 min-h-0 overflow-auto', contentClassName)}>
          {children}
        </div>
      </div>

      {/* Proactive tour prompt — rendered at the shell level so any section
          that passes tourPromptConfig gets the popup without needing its own
          TourPrompt instance. */}
      {tourPromptConfig && tourPromptContextKey && (
        <TourPrompt
          config={tourPromptConfig}
          contextKey={tourPromptContextKey}
          availableFlows={tourPromptAvailableFlows}
        />
      )}

      {onboardingViewScope && (
        <GuidedTour
          moduleKey={onboardingViewScope.module}
          activeTab={onboardingViewScope.activeTab}
          onNavigate={onboardingViewScope.onNavigate}
          autoFlowId={onboardingViewScope.viewId}
          navigableTabs={onboardingViewScope.navigableTabs}
        />
      )}
    </div>
    </StrictNewActionContext.Provider>
  );
}